import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudRainWind,
  CloudHail, CloudSnow, Snowflake, CloudLightning, Loader2, ArrowLeft,
  Droplets, Wind, Navigation, Moon, CloudMoon,
} from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";
import AccountUpsell from "@/components/AccountUpsell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  sun: Sun,
  "cloud-sun": CloudSun,
  cloud: Cloud,
  "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle,
  "cloud-rain": CloudRain,
  "cloud-rain-wind": CloudRainWind,
  "cloud-hail": CloudHail,
  "cloud-snow": CloudSnow,
  snowflake: Snowflake,
  "cloud-lightning": CloudLightning,
  // Night variants — backend sends these when is_night=true for clear-sky
  // conditions. We use a cooler palette so the hero card reads as "evening"
  // at a glance instead of "sunny but wrong color".
  moon: Moon,
  "cloud-moon": CloudMoon,
};

// Tailwind color classes per icon — gives the same warm/cool/dramatic palette
// the email's emojis convey, without leaving our brutalist black-and-yellow scheme.
const ICON_COLOR = {
  sun: "text-amber-500",
  "cloud-sun": "text-amber-400",
  cloud: "text-slate-400",
  "cloud-fog": "text-slate-400",
  "cloud-drizzle": "text-sky-500",
  "cloud-rain": "text-sky-600",
  "cloud-rain-wind": "text-sky-700",
  "cloud-hail": "text-sky-600",
  "cloud-snow": "text-sky-300",
  snowflake: "text-sky-300",
  "cloud-lightning": "text-amber-500",
  moon: "text-indigo-400",
  "cloud-moon": "text-indigo-400",
};

// Hero-card tints per dominant condition. Each is a soft gradient + a strong
// accent so the page doesn't read as "stark white" anymore.
const HERO_THEME = {
  sun:               { bg: "bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-100", accent: "bg-amber-500", iconColor: "text-amber-500", label: "Sunny vibes" },
  "cloud-sun":       { bg: "bg-gradient-to-br from-amber-50 via-slate-50 to-sky-50", accent: "bg-amber-400", iconColor: "text-amber-400", label: "Mixed sun & cloud" },
  cloud:             { bg: "bg-gradient-to-br from-slate-100 via-slate-50 to-zinc-100", accent: "bg-slate-400", iconColor: "text-slate-500", label: "Overcast" },
  "cloud-fog":       { bg: "bg-gradient-to-br from-slate-100 via-zinc-50 to-stone-100", accent: "bg-slate-400", iconColor: "text-slate-500", label: "Fog & low vis" },
  "cloud-drizzle":   { bg: "bg-gradient-to-br from-sky-50 via-slate-50 to-cyan-50", accent: "bg-sky-500", iconColor: "text-sky-500", label: "Drizzle on/off" },
  "cloud-rain":      { bg: "bg-gradient-to-br from-sky-100 via-blue-50 to-cyan-100", accent: "bg-sky-600", iconColor: "text-sky-600", label: "Rain expected" },
  "cloud-rain-wind": { bg: "bg-gradient-to-br from-sky-200 via-blue-100 to-slate-100", accent: "bg-sky-700", iconColor: "text-sky-700", label: "Wet & windy" },
  "cloud-hail":      { bg: "bg-gradient-to-br from-sky-100 via-slate-100 to-blue-100", accent: "bg-sky-600", iconColor: "text-sky-600", label: "Hail risk" },
  "cloud-snow":      { bg: "bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50", accent: "bg-sky-300", iconColor: "text-sky-400", label: "Snow incoming" },
  snowflake:         { bg: "bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50", accent: "bg-sky-300", iconColor: "text-sky-400", label: "Cold & snowy" },
  "cloud-lightning": { bg: "bg-gradient-to-br from-violet-100 via-amber-50 to-yellow-100", accent: "bg-amber-500", iconColor: "text-amber-500", label: "Storms likely" },
  moon:              { bg: "bg-gradient-to-br from-indigo-100 via-slate-100 to-blue-100", accent: "bg-indigo-500", iconColor: "text-indigo-500", label: "Clear & quiet" },
  "cloud-moon":      { bg: "bg-gradient-to-br from-indigo-50 via-slate-100 to-zinc-100", accent: "bg-indigo-400", iconColor: "text-indigo-400", label: "Mixed clouds, overnight" },
};

function fmtHourLabel(iso, isFirst) {
  // iso = "2026-04-26T14:00" (no offset, location-local)
  const hh = parseInt(iso.slice(11, 13), 10);
  if (isFirst) return "Now";
  if (hh === 0) return "12 AM";
  if (hh === 12) return "12 PM";
  if (hh < 12) return `${hh} AM`;
  return `${hh - 12} PM`;
}

function fmtDayLabel(iso) {
  const d = new Date(iso + ":00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function summarize(hours, dayFromBackend) {
  if (!hours.length) return null;
  // Prefer the backend-computed day_summary (true daily high/low/condition
  // matching the daily forecast card) over the next-24-hour reduction.
  // The latter drifts when the user opens the detail page mid-afternoon
  // because today's morning low has already passed.
  const temps = hours.map((h) => h.temp_f).filter((v) => v != null);
  const fallbackHigh = Math.max(...temps);
  const fallbackLow = Math.min(...temps);
  const maxPrecip = Math.max(...hours.map((h) => h.precip_prob || 0));
  const maxWind = Math.max(...hours.map((h) => h.wind_mph || 0));
  // Find dominant condition + its icon (used for the hero card theme).
  const counts = {};
  const iconCounts = {};
  hours.forEach((h) => {
    counts[h.condition] = (counts[h.condition] || 0) + 1;
    if (h.icon) iconCounts[h.icon] = (iconCounts[h.icon] || 0) + 1;
  });
  const fallbackCondition = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const fallbackIcon = Object.entries(iconCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const high = dayFromBackend?.high_f ?? fallbackHigh;
  const low = dayFromBackend?.low_f ?? fallbackLow;
  const condition = dayFromBackend?.condition ?? fallbackCondition;
  const dominantIcon = dayFromBackend?.icon ?? fallbackIcon;
  // Iter 34: expose the next-24h range separately so the user can see
  // both numbers explicitly when they differ. The PRIMARY high/low
  // shown big always matches the daily forecast card; the 24h range
  // appears as a smaller secondary value beneath. Resolves the
  // recurring "the daily card says 82° but hourly says 77°" confusion.
  const has24hDelta = (
    dayFromBackend && (
      fallbackHigh !== dayFromBackend.high_f ||
      fallbackLow !== dayFromBackend.low_f
    )
  );
  return {
    high, low, maxPrecip, maxWind, condition, dominantIcon,
    next24High: fallbackHigh,
    next24Low: fallbackLow,
    has24hDelta,
  };
}

export default function HourlyPage() {
  const { token, subId, date } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const url = date
      ? `${API}/manage/${token}/subscriptions/${subId}/hourly?date=${date}`
      : `${API}/manage/${token}/subscriptions/${subId}/hourly`;
    axios.get(url)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Couldn't load the hourly forecast.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, subId, date]);

  const summary = useMemo(() => data && summarize(data.hours || [], data.day_summary), [data]);
  const hours = data?.hours || [];

  // Establish temperature scale for the strip's mini-bars
  const tempBounds = useMemo(() => {
    if (!hours.length) return { min: 0, max: 1 };
    const temps = hours.map((h) => h.temp_f).filter((v) => v != null);
    const min = Math.min(...temps);
    const max = Math.max(...temps);
    return { min: Math.floor(min - 2), max: Math.ceil(max + 2) };
  }, [hours]);

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={45} />
            <div>
              <div className="font-display text-2xl font-black tracking-tighter leading-none">Weathervane</div>
              <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-neutral-500 font-bold mt-1">Weather &amp; News <span className="mx-1">/</span> <span className="italic">BYO Coffee</span></div>
            </div>
          </Link>
          <Link to={`/manage/${token}`} className="ticker-label !text-[10px] hover:text-black inline-flex items-center gap-1" data-testid="hourly-back-link">
            <ArrowLeft className="w-3 h-3" /> Manage
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-10" data-testid="hourly-page">
        <AccountUpsell next={`/hourly/${token}/${subId}`} className="mb-7" />
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Loader2 className="w-8 h-8 animate-spin mb-4" />
            <div className="ticker-label mb-1">Building hourly forecast</div>
            <p className="text-sm text-neutral-700 max-w-sm">Pulling 6 models and ensembling 24 hours of detail for you. ~1 second.</p>
          </div>
        )}

        {!loading && error && (
          <div className="border border-black bg-white p-10 max-w-xl">
            <div className="ticker-label mb-2">Hmm</div>
            <h1 className="font-display text-3xl font-black tracking-tighter">{error}</h1>
            <Link to={`/manage/${token}`} className="btn-brutal inline-flex items-center gap-2 px-5 py-3 mt-6 text-xs tracking-[0.14em] uppercase font-semibold">Manage subscriptions</Link>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {summary && (() => {
              const theme = HERO_THEME[summary.dominantIcon] || HERO_THEME.sun;
              const HeroIcon = ICON_MAP[summary.dominantIcon] || Sun;
              return (
                <div
                  className={`relative overflow-hidden border-2 border-black mb-8 ${theme.bg}`}
                  data-testid="hourly-hero"
                >
                  <div className={`absolute top-0 left-0 right-0 h-2 ${theme.accent}`} />
                  <div className="relative p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-center">
                    <div className="min-w-0">
                      <div className="ticker-label mb-2">
                        {data.mode === "next-24" ? "Next 24 hours" : "All day"} &middot; ensembled from {data.sources_count || 5} of 6 hourly models
                      </div>
                      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter break-words" data-testid="hourly-location">
                        {data.label}
                      </h1>
                      {hours.length > 0 && (
                        <div className="mt-2 text-sm text-neutral-700">
                          {fmtDayLabel(hours[0].time)}
                          {hours.length > 1 && data.mode === "next-24" && hours[hours.length - 1].time.slice(0, 10) !== hours[0].time.slice(0, 10) && (
                            <> &rarr; {fmtDayLabel(hours[hours.length - 1].time)}</>
                          )}
                        </div>
                      )}
                      <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 bg-black text-white text-[10px] uppercase tracking-[0.18em] font-bold" data-testid="hourly-hero-label">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${theme.accent}`} />
                        {theme.label}
                      </div>
                      <div className="mt-6 flex items-end gap-6 sm:gap-8">
                        <div>
                          <div className="ticker-label !text-[10px] mb-1">High</div>
                          <div className="font-display text-5xl sm:text-6xl font-black leading-none tracking-tighter" data-testid="hourly-day-high">{summary.high}°</div>
                        </div>
                        <div>
                          <div className="ticker-label !text-[10px] mb-1">Low</div>
                          <div className="font-display text-3xl sm:text-4xl font-black leading-none tracking-tighter text-neutral-700" data-testid="hourly-day-low">{summary.low}°</div>
                        </div>
                        <div className="hidden sm:block">
                          <div className="ticker-label !text-[10px] mb-1 inline-flex items-center gap-1"><Droplets className="w-3 h-3" />Max</div>
                          <div className="font-display text-3xl font-black leading-none tracking-tighter text-neutral-700">{summary.maxPrecip}%</div>
                        </div>
                        <div className="hidden sm:block">
                          <div className="ticker-label !text-[10px] mb-1 inline-flex items-center gap-1"><Wind className="w-3 h-3" />Wind</div>
                          <div className="font-display text-3xl font-black leading-none tracking-tighter text-neutral-700">{summary.maxWind}<span className="text-base font-bold ml-1">mph</span></div>
                        </div>
                      </div>
                      {/* Secondary 24h range. Shown only when it
                          differs from the daily-summary numbers above
                          — otherwise it's redundant. Resolves the
                          "daily says X, hourly says Y" confusion by
                          surfacing BOTH values with clear labels. */}
                      {summary.has24hDelta && (
                        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-neutral-600" data-testid="hourly-next24-range">
                          <span className="ticker-label !text-[10px] !font-bold !text-neutral-500">Next 24h</span>
                          <span className="font-mono font-semibold text-neutral-700">{summary.next24High}°–{summary.next24Low}°</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-center sm:justify-end">
                      <div className="relative">
                        <div className={`absolute inset-0 ${theme.accent} opacity-25 blur-3xl rounded-full`} />
                        <HeroIcon
                          className={`relative w-32 h-32 sm:w-44 sm:h-44 ${theme.iconColor} drop-shadow-sm`}
                          strokeWidth={1.6}
                          data-testid="hourly-hero-icon"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Compact secondary stats on mobile (hidden ≥sm because shown above) */}
                  <div className="grid grid-cols-2 sm:hidden border-t-2 border-black">
                    <div className="px-5 py-3 border-r-2 border-black">
                      <div className="ticker-label !text-[10px] mb-1 inline-flex items-center gap-1"><Droplets className="w-3 h-3" />Max precip</div>
                      <div className="font-display text-2xl font-black leading-none tracking-tighter">{summary.maxPrecip}%</div>
                    </div>
                    <div className="px-5 py-3">
                      <div className="ticker-label !text-[10px] mb-1 inline-flex items-center gap-1"><Wind className="w-3 h-3" />Max wind</div>
                      <div className="font-display text-2xl font-black leading-none tracking-tighter">{summary.maxWind} mph</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Moon phase + Wind summary panel */}
            {(data.moon || (summary && summary.maxWind != null)) && (() => {
              const moon = data.moon;
              // Dominant wind direction across the window — pick the most common compass.
              const dirCounts = {};
              hours.forEach((h) => { if (h.wind_dir_compass) dirCounts[h.wind_dir_compass] = (dirCounts[h.wind_dir_compass] || 0) + 1; });
              const dominantDir = Object.entries(dirCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
              const avgWind = hours.length
                ? Math.round(hours.reduce((a, h) => a + (h.wind_mph || 0), 0) / hours.length)
                : null;
              // Compass arrow rotation: deg from a representative hour with that compass.
              const sampleDeg = hours.find((h) => h.wind_dir_compass === dominantDir)?.wind_dir_deg;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-testid="hourly-extras">
                  {moon && (
                    <div className="border border-black bg-white p-5 flex items-center gap-5" data-testid="hourly-moon">
                      <MoonGlyph
                        illuminationPct={moon.illumination_pct}
                        ageDays={moon.age_days}
                        size={64}
                      />
                      <div className="min-w-0">
                        <div className="ticker-label !text-[10px] mb-1">Moon phase</div>
                        <div className="font-display text-2xl font-black tracking-tighter leading-none" data-testid="hourly-moon-phase">{moon.phase}</div>
                        <div className="text-xs text-neutral-600 mt-1.5 tabular-nums">
                          {moon.illumination_pct}% illuminated · {moon.age_days}d into cycle
                        </div>
                      </div>
                    </div>
                  )}
                  {avgWind != null && (
                    <div className="border border-black bg-white p-5 flex items-center gap-5" data-testid="hourly-wind">
                      <div className="relative w-16 h-16 shrink-0 border border-black/20 rounded-full bg-[#FFFBE6] flex items-center justify-center">
                        <span className="absolute top-1 text-[9px] font-bold tracking-widest text-neutral-500">N</span>
                        <span className="absolute bottom-1 text-[9px] font-bold tracking-widest text-neutral-300">S</span>
                        <span className="absolute left-1.5 text-[9px] font-bold tracking-widest text-neutral-300">W</span>
                        <span className="absolute right-1.5 text-[9px] font-bold tracking-widest text-neutral-300">E</span>
                        <Navigation
                          className="w-6 h-6 text-[#0A0A0A] fill-current"
                          strokeWidth={1.5}
                          style={sampleDeg != null ? { transform: `rotate(${sampleDeg}deg)` } : undefined}
                          data-testid="hourly-wind-compass"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="ticker-label !text-[10px] mb-1">Wind</div>
                        <div className="font-display text-2xl font-black tracking-tighter leading-none">
                          <span data-testid="hourly-wind-avg">{avgWind} mph</span>
                          {dominantDir && (
                            <span className="text-base font-bold text-neutral-700 ml-2" data-testid="hourly-wind-dir">{dominantDir}</span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-600 mt-1.5 tabular-nums">
                          Avg over window · gusting to {summary.maxWind} mph
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Apple-Weather-style horizontal strip */}
            <div className="border-2 border-black bg-white">
              <div className="px-5 py-3 border-b border-black bg-[#0A0A0A] text-white flex items-center justify-between">
                <div className="ticker-label !text-[10px] !text-[#FFD600] !tracking-[0.22em]">Hourly timeline</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-400">scroll &rarr;</div>
              </div>
              <div className="overflow-x-auto" data-testid="hourly-strip">
                <div className="flex">
                  {hours.map((h, i) => (
                    <HourCell
                      key={h.time}
                      h={h}
                      isFirst={i === 0 && data.mode === "next-24"}
                      bounds={tempBounds}
                      isDayBoundary={i > 0 && hours[i - 1].time.slice(8, 10) !== h.time.slice(8, 10)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Detail grid below the strip */}
            <div className="mt-8 border border-black bg-white">
              <div className="grid grid-cols-[52px_22px_1fr_44px_46px_64px] sm:grid-cols-[80px_28px_1fr_72px_72px_92px_72px] gap-x-2 sm:gap-x-5 px-3 sm:px-6 py-3 text-[10px] uppercase tracking-[0.18em] text-neutral-500 border-b border-black/10">
                <div>Hour</div>
                <div></div>
                <div>Cond.</div>
                <div className="text-right">Temp</div>
                <div className="text-right">Precip</div>
                <div className="text-right">Wind</div>
                <div className="text-right hidden sm:block">Humidity</div>
              </div>
              {hours.map((h, i) => {
                const Icon = ICON_MAP[h.icon] || Sun;
                return (
                  <div
                    key={`row-${h.time}`}
                    className="grid grid-cols-[52px_22px_1fr_44px_46px_64px] sm:grid-cols-[80px_28px_1fr_72px_72px_92px_72px] gap-x-2 sm:gap-x-5 px-3 sm:px-6 py-3 items-center border-b border-black/5 last:border-b-0"
                    data-testid={`hourly-row-${i}`}
                  >
                    <div className="font-display font-black tracking-tight text-sm sm:text-base">{fmtHourLabel(h.time, i === 0 && data.mode === "next-24")}</div>
                    <div className="flex items-center justify-center">
                      <Icon className={`w-5 h-5 shrink-0 ${ICON_COLOR[h.icon] || "text-neutral-700"}`} strokeWidth={2.25} />
                    </div>
                    <div className="text-xs sm:text-sm truncate">{h.condition}</div>
                    <div className="text-right font-display font-black text-base sm:text-lg">{h.temp_f != null ? `${h.temp_f}°` : "—"}</div>
                    <div className="text-right text-xs sm:text-sm tabular-nums" data-testid={`hourly-row-${i}-precip`}>{h.precip_prob}%</div>
                    <div className="text-right text-xs sm:text-sm tabular-nums whitespace-nowrap" data-testid={`hourly-row-${i}-wind`}>
                      {h.wind_mph ?? "—"}<span className="text-neutral-400 text-[10px] sm:text-xs ml-0.5 sm:ml-1">mph</span>
                      {h.wind_dir_compass && (
                        <span className="text-neutral-500 text-[10px] sm:text-xs ml-1 hidden sm:inline">{h.wind_dir_compass}</span>
                      )}
                    </div>
                    <div className="text-right text-sm tabular-nums hidden sm:block">{h.humidity_pct ?? "—"}%</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 text-xs text-neutral-500">
              Built from {data.sources_count || 5} of 6 ensembled models (Open-Meteo, ECMWF, GFS, ICON, NAM, MET Norway). Hourly uses high-res Open-Meteo outputs;
              days 5-7 are inherently less reliable.
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon }) { // eslint-disable-line no-unused-vars
  return (
    <div className="border border-black bg-white p-4">
      <div className="ticker-label !text-[10px] mb-2 inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
    </div>
  );
}

function HourCell({ h, isFirst, bounds, isDayBoundary }) {
  const Icon = ICON_MAP[h.icon] || Sun;
  // Position the temp label so its vertical alignment hints at relative warmth
  const range = Math.max(1, bounds.max - bounds.min);
  const ratio = h.temp_f != null ? (h.temp_f - bounds.min) / range : 0.5;
  const barH = Math.max(6, Math.round(ratio * 56));
  const wetBar = Math.max(0, Math.min(56, (h.precip_prob || 0) * 0.56));
  return (
    <div
      className={`shrink-0 w-[78px] flex flex-col items-center px-1 py-4 ${isDayBoundary ? "border-l-2 border-[#0A0A0A] bg-[#FFFBE6]" : "border-l border-black/10"}`}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-500">
        {isFirst ? "Now" : fmtHourLabel(h.time, false)}
      </div>
      {isDayBoundary && (
        <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#0A0A0A] mt-1">{fmtDayLabel(h.time)}</div>
      )}
      <Icon className={`w-6 h-6 my-2 ${ICON_COLOR[h.icon] || "text-neutral-700"}`} strokeWidth={2.25} />
      <div className="font-display font-black text-lg leading-none">{h.temp_f != null ? `${h.temp_f}°` : "—"}</div>
      <div className="mt-3 w-full h-[60px] flex items-end gap-1 px-1">
        <div className="flex-1 bg-[#0A0A0A]" style={{ height: `${barH}px` }} title={`${h.temp_f}° relative to range`} />
        {wetBar > 2 && (
          <div className="w-1.5 bg-[#1d4ed8] opacity-80" style={{ height: `${wetBar}px` }} title={`${h.precip_prob}% precipitation`} />
        )}
      </div>
      <div className="mt-2 text-[10px] tabular-nums text-neutral-500 inline-flex items-center gap-1">
        <Droplets className="w-3 h-3" /> {h.precip_prob}%
      </div>
      <div className="text-[10px] tabular-nums text-neutral-500 inline-flex items-center gap-1">
        <Wind className="w-3 h-3" /> {h.wind_mph ?? "—"}
      </div>
    </div>
  );
}

/**
 * Photo-realistic moon glyph. Uses an SVG mask: full-circle disc in indigo,
 * with the lit portion shown via an elliptical mask whose horizontal radius
 * varies with `ageDays` (0 = new, ~14.77 = full, ~29.53 = back to new).
 *
 * Pixel-perfect at any resolution and crisp on retina, unlike the previous
 * clipPath-on-divs approach which aliased the lit edge.
 */
function MoonGlyph({ illuminationPct = 50, ageDays = 0, size = 64 }) {
  const SYNODIC = 29.5305882;
  const phaseFrac = ((ageDays % SYNODIC) + SYNODIC) % SYNODIC / SYNODIC; // 0..1
  // Waxing if phase < 0.5 (lit side on right). Waning otherwise (lit side on left).
  const waxing = phaseFrac < 0.5;
  // Cosine of phase angle: +1 at new (no lit), -1 at full (fully lit).
  // We use it to set the terminator ellipse rx — narrow ellipse near 0/0.5,
  // wide near 0.25/0.75 so the lit area transitions smoothly.
  const cos = Math.cos(phaseFrac * 2 * Math.PI);
  const r = 100;
  const rx = Math.abs(cos) * r; // terminator ellipse half-width
  const cx = waxing ? 100 : 100; // terminator centered at disc center
  const id = `moon-${illuminationPct}-${ageDays}`;
  // Fully dark at new (illum < 4) — no lit ellipse at all.
  const isNew = illuminationPct < 4;
  const isFull = illuminationPct > 96;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      className="shrink-0"
      data-testid="hourly-moon-glyph"
    >
      <defs>
        <radialGradient id={`${id}-lit`} cx="40%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#FFF7D6" />
          <stop offset="60%" stopColor="#F4DA9C" />
          <stop offset="100%" stopColor="#C9A857" />
        </radialGradient>
        <radialGradient id={`${id}-dark`} cx="60%" cy="50%" r="70%">
          <stop offset="0%" stopColor="#1F2A44" />
          <stop offset="100%" stopColor="#0A0F1E" />
        </radialGradient>
        <mask id={`${id}-mask`}>
          <rect width="200" height="200" fill="black" />
          {isFull ? (
            <circle cx="100" cy="100" r={r} fill="white" />
          ) : isNew ? null : waxing ? (
            // Waxing: right half lit, minus a dark ellipse that shrinks toward 50%.
            <>
              <path d="M100,0 a100,100 0 0 1 0,200 z" fill="white" />
              <ellipse cx={cx} cy="100" rx={rx} ry={r} fill={phaseFrac < 0.25 ? "black" : "white"} />
            </>
          ) : (
            // Waning: left half lit.
            <>
              <path d="M100,0 a100,100 0 0 0 0,200 z" fill="white" />
              <ellipse cx={cx} cy="100" rx={rx} ry={r} fill={phaseFrac > 0.75 ? "black" : "white"} />
            </>
          )}
        </mask>
      </defs>
      {/* Dark side of the moon (always full disc as base) */}
      <circle cx="100" cy="100" r={r} fill={`url(#${id}-dark)`} />
      {/* Lit portion stenciled via mask */}
      <circle cx="100" cy="100" r={r} fill={`url(#${id}-lit)`} mask={`url(#${id}-mask)`} />
      {/* Subtle outer ring to ground it on white card backgrounds */}
      <circle cx="100" cy="100" r={r - 0.5} fill="none" stroke="#0A0A0A" strokeOpacity="0.18" strokeWidth="1.5" />
    </svg>
  );
}

